import { invalidateAllQueries } from "bonzai"
import { useGraphqlQuery } from "graphql-mock"
import { useModal } from "hooks"
import { Dialog, IconButton, useWindowWidth } from "materia"
import {
  createContext,
  useContext,
  useMemo,
  useState,
  type PropsWithChildren,
  type ReactNode,
} from "react"
import { createPortal } from "react-dom"
import { ATTENSI_PRODUCT_PLATFORM_FOR_OS } from "../../constants"
import { useSubscribeToVuplex } from "../../vuplex/subscribeToVuplex"
import { query } from "./gql"
import {
  ProductModalActivity,
  ProductModalActivityLoader,
} from "./ProductModalActivity"
import { ProductModalButtons } from "./ProductModalButtons"
import { ProductModalError } from "./ProductModalError"
import { ProductModalEventLoader } from "./ProductModalEvent/ProductModalEventLoader"
import { ProductModalLeaderboardLoader } from "./ProductModalLeaderboard/ProductModalLeaderboardLoader"
import { ProductModalMain, ProductModalMainSkeleton } from "./ProductModalMain"

import s from "./ProductModal.module.scss"

type ContextType = {
  targetNode: HTMLDivElement | null
}

const ButtonsContext = createContext<ContextType | null>(null)

export const ProductModal = ({
  closeRoute,
  sideContent,
  productId,
}: {
  closeRoute: string
  sideContent: "leaderboard" | "activity" | "event" | "none"
  productId: string
}) => {
  const { data, error, isPending } = useGraphqlQuery(query, {
    productId,
    platform: ATTENSI_PRODUCT_PLATFORM_FOR_OS,
  })

  const { onClose } = useModal(closeRoute, () => {
    invalidateAllQueries()
  })

  useSubscribeToVuplex("CLOSE_PRODUCT_MODALS", onClose)

  if (error) return <ProductModalError error={error} onClose={onClose} />

  return (
    <Dialog className={s.modal} ariaLabel="Play product" onClose={onClose}>
      <Content>
        {isPending ? (
          <>
            <ProductModalMainSkeleton />
            {sideContent !== "none" && <ProductModalActivity.Skeleton />}
          </>
        ) : (
          <>
            <ProductModalMain
              data={data.productModalMain}
              productModalButtons={
                <ProductModalButtons data={data.productModalButtons} />
              }
            />

            {sideContent === "leaderboard" &&
              !data.currentCompany.portalFeatureFlags.includes(
                "disable_leaderboard"
              ) && (
                <ProductModalLeaderboardLoader productId={Number(productId)} />
              )}

            {sideContent === "activity" && !data.product.isLocked && (
              <ProductModalActivityLoader
                productId={Number(productId)}
                onClose={onClose}
              />
            )}

            {sideContent === "event" && !data.product.isLocked && (
              <ProductModalEventLoader productId={Number(productId)} />
            )}

            <IconButton
              as="button"
              className={s.closeButton}
              icon="Close"
              onClick={onClose}
              aria-label="Close"
            />
          </>
        )}
      </Content>
    </Dialog>
  )
}

const Content = ({ children }: { children: ReactNode }) => {
  const { isBigWindow } = useWindowWidth()

  const [refState, setRefState] = useState<HTMLDivElement | null>(null)
  const contextValue = useMemo(() => ({ targetNode: refState }), [refState])

  return (
    <ButtonsContext.Provider value={contextValue}>
      <div className={s.content}>
        <div className={s.content__scrollContainer}>
          {children}
          {!isBigWindow && <div ref={setRefState} />}
        </div>
      </div>
    </ButtonsContext.Provider>
  )
}

const Main = ({ children }: PropsWithChildren) => (
  <div className={s.main}>{children}</div>
)

const Side = ({ children }: PropsWithChildren) => (
  <div className={s.side}>{children}</div>
)

const ActionButtons = ({ children }: PropsWithChildren) => {
  const contextValue = useContext(ButtonsContext)
  const { isBigWindow } = useWindowWidth()

  if (!contextValue || isBigWindow) {
    return <>{children}</>
  }

  const { targetNode } = contextValue
  if (!targetNode) return null

  const element = (
    <div className={s.actionButtons}>
      <div className={s.actionButtons__content}>{children}</div>
    </div>
  )

  return createPortal(element, targetNode)
}

ProductModal.Main = Main
ProductModal.Side = Side
ProductModal.ActionButtons = ActionButtons
